/**
 * The personal API tokens of the section "API-Tokens" on "Mein Profil": what the server lists and
 * returns, the validities, and the backend's refusal codes. Kept out of the element so it is testable
 * without a DOM, together with which text a refusal shows (`apiTokenErrorKey`).
 */

import { OWN_PROFILE_ERROR_CODES } from './own-profile.state';
import { refusalKey } from './refusal-key';

/** One token as `GET /api/v1/me/api-tokens` lists it — never the token itself. Times are ISO-8601 UTC. */
export interface OwnApiToken {
    uid: string;
    name: string;
    prefix: string;
    createdAt: string;
    expiresAt: string;
    lastUsedAt: string | null;
    revokedAt: string | null;
    status: 'active' | 'expired' | 'revoked';
}

/** The answer of `POST /api/v1/me/api-tokens`: the one time the token itself is shown. */
export interface CreatedApiToken {
    uid: string;
    name: string;
    prefix: string;
    expiresAt: string;
    token: string;
}

/** The validities a token can be created with (Stefan, 2026-10-25), and the preselected one. */
export const OWN_API_TOKEN_VALIDITY_DAYS = [30, 90, 365] as const;
export const OWN_API_TOKEN_DEFAULT_DAYS = 90;
/** At most this many tokens can be active at the same time (CreateApiTokenCommandHandler::MAX_ACTIVE). */
export const OWN_API_TOKEN_MAX_ACTIVE = 10;

/**
 * The backend's refusal codes of creating and revoking a token: the shared ones of the own-record rule
 * and the current-password check, and those of CreateApiTokenCommandHandler / RevokeApiTokenCommandHandler.
 */
export const OWN_API_TOKEN_ERROR_CODES = {
    otherUser: OWN_PROFILE_ERROR_CODES.otherUser,
    wrongPassword: OWN_PROFILE_ERROR_CODES.wrongPassword,
    passwordChecksLocked: OWN_PROFILE_ERROR_CODES.passwordChecksLocked,
    invalidName: 1792742400030,
    invalidValidity: 1792742400031,
    limitReached: 1792742400032,
    noSuchToken: 1792742400033,
} as const;

export type OwnApiTokenErrorKey = keyof typeof OWN_API_TOKEN_ERROR_CODES | 'forbidden' | 'failed';

/** Which text a refused token creation or revocation shows; the same rules as the profile's refusals. */
export function apiTokenErrorKey(error: unknown): OwnApiTokenErrorKey {
    return refusalKey(error, OWN_API_TOKEN_ERROR_CODES);
}
